import { httpService } from '../utils/http';
import { 
  ApiResponse, 
  Page, 
  Dealer,
  DealerVO,
  DealerRequest,
  DealerQueryRequest,
} from '../types';

/**
 * 经销商管理服务类
 * 提供经销商的创建、更新、查询、启用/禁用、删除等功能
 */
export class DealerService {
  // ==================== 经销商创建 ====================

  /**
   * 创建经销商（实体方式）
   * @param data 经销商数据
   * @returns Promise<ApiResponse<Dealer>>
   */
  async createDealer(data: Dealer): Promise<ApiResponse<Dealer>> {
    return httpService.post('/dealer', data);
  }

  /**
   * 创建经销商（DTO方式）- 推荐使用
   * @param data 经销商数据
   * @param createBy 创建人
   * @returns Promise<ApiResponse<Dealer>>
   */
  async createDealerDto(data: DealerRequest, createBy: string): Promise<ApiResponse<Dealer>> {
    return httpService.post(`/dealer/dto?createBy=${encodeURIComponent(createBy)}`, data);
  }

  // ==================== 经销商更新 ====================

  /**
   * 更新经销商（实体方式）
   * @param id 经销商ID
   * @param data 经销商数据
   * @returns Promise<ApiResponse<Dealer>>
   */
  async updateDealer(id: number, data: Dealer): Promise<ApiResponse<Dealer>> {
    return httpService.put(`/dealer/${id}`, data);
  }

  /**
   * 更新经销商（DTO方式）- 推荐使用
   * @param id 经销商ID
   * @param data 经销商数据
   * @param updateBy 更新人
   * @returns Promise<ApiResponse<Dealer>>
   */
  async updateDealerDto(id: number, data: DealerRequest, updateBy: string): Promise<ApiResponse<Dealer>> {
    return httpService.put(`/dealer/${id}/dto?updateBy=${encodeURIComponent(updateBy)}`, data);
  }

  // ==================== 经销商查询 ====================

  /**
   * 根据ID查询经销商详情
   * @param id 经销商ID
   * @returns Promise<ApiResponse<Dealer>>
   */
  async getDealerById(id: number): Promise<ApiResponse<Dealer>> {
    return httpService.get(`/dealer/${id}`);
  }

  /**
   * 根据ID查询经销商VO详情（包含文本字段）
   * @param id 经销商ID
   * @returns Promise<ApiResponse<DealerVO>>
   */
  async getDealerVoById(id: number): Promise<ApiResponse<DealerVO>> {
    return httpService.get(`/dealer/${id}/vo`);
  }

  /**
   * 分页查询经销商
   * @param page 页码（从0开始）
   * @param size 每页大小
   * @param sort 排序字段和方向，格式：字段名,方向
   * @returns Promise<ApiResponse<Page<Dealer>>>
   */
  async getDealerList(page: number = 0, size: number = 10, sort: string = 'id,desc'): Promise<ApiResponse<Page<Dealer>>> {
    return httpService.get('/dealer', { page, size, sort });
  }

  /**
   * 根据查询条件分页查询经销商
   * @param params 查询参数
   * @returns Promise<ApiResponse<Page<DealerVO>>>
   */
  async queryDealers(params: DealerQueryRequest): Promise<ApiResponse<Page<DealerVO>>> {
    const { pageNo = 0, pageSize = 10, sortField, sortDirection, ...queryParams } = params;
    const sort = sortField && sortDirection ? `${sortField},${sortDirection}` : 'id,desc';
    return httpService.post(`/dealer/query?page=${pageNo}&size=${pageSize}&sort=${sort}`, queryParams);
  }

  /**
   * 根据经销商编码查询
   * @param dealerCode 经销商编码
   * @returns Promise<ApiResponse<Dealer>>
   */
  async getDealerByCode(dealerCode: string): Promise<ApiResponse<Dealer>> {
    return httpService.get(`/dealer/code/${encodeURIComponent(dealerCode)}`);
  }

  /**
   * 根据管理员ID查询经销商
   * @param adminId 管理员ID
   * @returns Promise<ApiResponse<Dealer>>
   */
  async getDealerByAdminId(adminId: number): Promise<ApiResponse<Dealer>> {
    return httpService.get(`/dealer/admin/${adminId}`);
  }

  /**
   * 根据状态查询经销商列表
   * @param status 状态：1-启用，0-禁用
   * @returns Promise<ApiResponse<Dealer[]>>
   */
  async getDealersByStatus(status: number): Promise<ApiResponse<Dealer[]>> {
    return httpService.get(`/dealer/status/${status}`);
  }

  /**
   * 根据经销商等级查询列表
   * @param dealerLevel 经销商等级
   * @returns Promise<ApiResponse<Dealer[]>>
   */
  async getDealersByLevel(dealerLevel: number): Promise<ApiResponse<Dealer[]>> {
    return httpService.get(`/dealer/level/${dealerLevel}`);
  }

  /**
   * 根据经销商名称模糊搜索
   * @param dealerName 经销商名称
   * @returns Promise<ApiResponse<Dealer[]>>
   */
  async searchDealers(dealerName: string): Promise<ApiResponse<Dealer[]>> {
    return httpService.get('/dealer/search', { dealerName: encodeURIComponent(dealerName) });
  }

  // ==================== 经销商状态管理 ====================

  /**
   * 启用经销商
   * @param id 经销商ID
   * @param updateBy 更新人
   * @returns Promise<ApiResponse<void>>
   */
  async enableDealer(id: number, updateBy: string): Promise<ApiResponse<void>> {
    return httpService.put(`/dealer/${id}/enable?updateBy=${encodeURIComponent(updateBy)}`);
  }

  /**
   * 禁用经销商
   * @param id 经销商ID
   * @param updateBy 更新人
   * @returns Promise<ApiResponse<void>>
   */
  async disableDealer(id: number, updateBy: string): Promise<ApiResponse<void>> {
    return httpService.put(`/dealer/${id}/disable?updateBy=${encodeURIComponent(updateBy)}`);
  }

  /**
   * 批量更新经销商状态
   * @param ids 经销商ID列表
   * @param status 状态：1-启用，0-禁用
   * @param updateBy 更新人
   * @returns Promise<ApiResponse<void>>
   */
  async batchUpdateStatus(ids: number[], status: number, updateBy: string): Promise<ApiResponse<void>> {
    const idsParam = ids.join(',');
    return httpService.put(`/dealer/batch/status?ids=${idsParam}&status=${status}&updateBy=${encodeURIComponent(updateBy)}`);
  }

  // ==================== 经销商删除 ====================

  /**
   * 删除经销商
   * @param id 经销商ID
   * @param updateBy 更新人
   * @returns Promise<ApiResponse<void>>
   */
  async deleteDealer(id: number, updateBy: string): Promise<ApiResponse<void>> {
    return httpService.delete(`/dealer/${id}?updateBy=${encodeURIComponent(updateBy)}`);
  }
}

// 创建默认实例
export const dealerService = new DealerService();

// 导出默认实例
export default dealerService;

